import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  selectCurrentUser,
  selectUserId,
  selectUsers,
  setCurrentUser,
  setIsLogOut,
  setName,
  setUsers,
} from '../login/loginSlice';
import {
  assignEmployeeCompany,
  getBusinessCompanies,
  getBusinessEmployees,
  getEmployeeCompanyAccess,
  getUserData,
  inviteBusinessEmployee,
  lookupEmployeeByPhone,
  removeBusinessEmployee,
  saveEmployeeCompanyAccess,
  updateUserProfile,
  uploadUserPhoto,
  archiveAccountCompanies,
} from '../login/loginApi';
import { resolveBusinessAccountId } from '../../utils/accountUtils';
import {
  arrowLIcon,
  arrowRIcon,
  closeIconTask,
  deleteIcon,
  dots3VerticalIcon,
  pencilEditIcon,
  addAccountIcon,
  usersGroupIcon,
  iconWithPlus,
} from '../../static/svgs';
import { url } from '../../static/env';
import ClientMenuModal from '../client/ClientMenuModal';
import DeleteModal from '../client/DeleteModal';
import ProfileCompanies from './ProfileCompanies';
import { createTask } from '../tasks/taskApi';
import { useSectionMenuReset } from '../../context/SectionMenuResetContext';
import '../tasks/task.css';
import '../shared/companyPicker.css';
import '../client/client.css';
import './profile.css';

const DOC_ACCESS_SECTIONS = [
  { id: 'invoices', title: 'Счета-фактуры, УПД, акты, накладные, чеки' },
  { id: 'bank', title: 'Банковские выписки' },
  { id: 'tax', title: 'Налоги, декларации' },
  { id: 'salary', title: 'Заработная плата, взносы' },
  { id: 'buh_bill', title: 'Счета за бухгалтерские услуги' },
];

function mapDocAccessFromApi(access) {
  const docs = {
    invoices: false,
    bank: false,
    tax: false,
    salary: false,
    buh_bill: false,
  };
  DOC_ACCESS_SECTIONS.forEach(({ id }) => {
    docs[id] = !!(access?.[id] ?? access?.[`access_${id}`]);
  });
  if (
    access?.documents &&
    !DOC_ACCESS_SECTIONS.some(({ id }) => docs[id])
  ) {
    DOC_ACCESS_SECTIONS.forEach(({ id }) => {
      docs[id] = true;
    });
  }
  return docs;
}

function mapApiEmployee(row) {
  const companies = (row.companies || []).map((c) => ({
    companyId: String(c.company_id),
    legacyUserId: c.legacy_user_id != null ? String(c.legacy_user_id) : null,
    name: c.name || `Компания #${c.company_id}`,
    inn: c.inn || null,
  }));
  return {
    id: String(row.id),
    employeeUserId: row.employee_user_id,
    displayName: row.name || row.phone || `Сотрудник ${row.employee_user_id}`,
    phone: row.phone,
    status: row.status,
    companies,
  };
}

function splitPersonName(fullName) {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
}

function personInitials(fullName) {
  const { firstName, lastName } = splitPersonName(fullName);
  const a = (firstName[0] || '').toUpperCase();
  const b = (lastName[0] || firstName[1] || '').toUpperCase();
  return `${a}${b}` || '?';
}

function ProfileAvatar({
  name,
  photoPath,
  size = 72,
  onClick,
  interactive = false,
  emptyGreen = false,
}) {
  const src = photoPath ? `${url}/${photoPath}` : null;
  const className = [
    'profile-avatar',
    interactive ? 'profile-avatar--interactive' : '',
    !src && emptyGreen ? 'profile-avatar--empty-green' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const style = { width: size, height: size, fontSize: Math.round(size * 0.34) };
  if (src) {
    return (
      <button
        type="button"
        className={className}
        style={style}
        onClick={onClick}
        disabled={!interactive}
        aria-label={interactive ? 'Изменить фото' : undefined}
      >
        <img src={src} alt="" className="profile-avatar-img" />
      </button>
    );
  }
  return (
    <button
      type="button"
      className={`${className} profile-avatar--initials`}
      style={style}
      onClick={onClick}
      disabled={!interactive}
      aria-label={interactive ? 'Установить фото' : undefined}
    >
      {personInitials(name)}
    </button>
  );
}

function Profile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(selectUserId);
  const users = useSelector(selectUsers);
  const currentUser = useSelector(selectCurrentUser);
  const fileInputRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState(null);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [inviteValue, setInviteValue] = useState('');
  const [employeeLookup, setEmployeeLookup] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessDraft, setAccessDraft] = useState(null);
  const [accessBaseline, setAccessBaseline] = useState(null);
  const [accountCompanies, setAccountCompanies] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [employeeMenuOpen, setEmployeeMenuOpen] = useState(false);
  const [employeesListMenuOpen, setEmployeesListMenuOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hubSection, setHubSection] = useState(null); // null | 'companies' | 'employees'
  const [profileSaving, setProfileSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [photoPath, setPhotoPath] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [displayPhone, setDisplayPhone] = useState('');

  useSectionMenuReset('/profile', () => {
    if (!hubSection && !editOpen) {
      return false;
    }
    setHubSection(null);
    setEditOpen(false);
    setMenuOpen(false);
    setEmployeeMenuOpen(false);
    setEmployeesListMenuOpen(false);
    setDeleteOpen(false);
    setSelectedEmployeeId(null);
    setSelectedCompanyKey(null);
    setAccessDraft(null);
    setAccessBaseline(null);
    setShowAddEmployeeModal(false);
    setShowAddCompanyModal(false);
    return true;
  });

  const businessAccountId = useMemo(
    () => resolveBusinessAccountId({ users, currentUser, userId }),
    [users, currentUser, userId]
  );

  const syncProfileFromSources = useCallback((userRow) => {
    const accountUser =
      currentUser && !currentUser.is_company_context && !currentUser.is_company_record
        ? currentUser
        : null;
    const loginRow = (users || []).find(
      (u) => Number(u.user_id) === Number(businessAccountId) && !u.is_company_context
    );
    const name =
      userRow?.name ||
      accountUser?.name ||
      loginRow?.company_name ||
      '';
    const phone =
      userRow?.phone ||
      accountUser?.phone ||
      loginRow?.username ||
      '';
    const photo =
      userRow?.photo_path ||
      accountUser?.photo_path ||
      loginRow?.photo_path ||
      null;
    setDisplayName(name);
    setDisplayPhone(phone);
    setPhotoPath(photo || null);
    const split = splitPersonName(name);
    setFirstName(split.firstName);
    setLastName(split.lastName);
  }, [businessAccountId, currentUser, users]);

  useEffect(() => {
    syncProfileFromSources(null);
  }, [syncProfileFromSources]);

  useEffect(() => {
    if (!businessAccountId) return;
    getUserData(businessAccountId)
      .then((data) => {
        const row = data?.user?.[0];
        if (row) syncProfileFromSources(row);
      })
      .catch(() => {});
  }, [businessAccountId, syncProfileFromSources]);

  useEffect(() => {
    if (!businessAccountId) {
      setAccountCompanies([]);
      return;
    }
    getBusinessCompanies(businessAccountId)
      .then((result) => {
        if (!result.success) {
          setAccountCompanies([]);
          return;
        }
        setAccountCompanies(
          (result.companies || []).map((c) => ({
            companyId: String(c.company_id),
            legacyUserId: c.legacy_user_id != null ? String(c.legacy_user_id) : null,
            name: c.name || `Компания #${c.company_id}`,
            inn: c.inn || null,
          }))
        );
      })
      .catch(() => setAccountCompanies([]));
  }, [businessAccountId]);

  const invitePhoneDigits = useMemo(
    () => String(inviteValue || '').replace(/\D/g, ''),
    [inviteValue]
  );

  useEffect(() => {
    if (!showAddEmployeeModal) {
      setEmployeeLookup(null);
      setLookupLoading(false);
      return undefined;
    }
    if (invitePhoneDigits.length !== 11 || !businessAccountId) {
      setEmployeeLookup(null);
      setLookupLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLookupLoading(true);
      try {
        const result = await lookupEmployeeByPhone(businessAccountId, invitePhoneDigits);
        if (!cancelled) {
          setEmployeeLookup(result);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setEmployeeLookup({ success: false, error: 'Не удалось найти пользователя' });
        }
      } finally {
        if (!cancelled) setLookupLoading(false);
      }
    }, 280);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showAddEmployeeModal, invitePhoneDigits, businessAccountId]);

  const closeAddEmployeeModal = () => {
    setShowAddEmployeeModal(false);
    setInviteValue('');
    setEmployeeLookup(null);
    setLookupLoading(false);
  };

  const canInviteEmployee =
    !!employeeLookup?.found &&
    !employeeLookup?.is_self &&
    !employeeLookup?.already_added &&
    !lookupLoading &&
    invitePhoneDigits.length === 11;

  const loadEmployees = useCallback(async () => {
    if (!businessAccountId) {
      setEmployees([]);
      return;
    }
    setIsLoading(true);
    setLoadError('');
    try {
      const result = await getBusinessEmployees(businessAccountId);
      if (result.success) {
        setEmployees((result.employees || []).map(mapApiEmployee));
      } else {
        setLoadError(result.error || 'Не удалось загрузить сотрудников');
        setEmployees([]);
      }
    } catch (e) {
      console.error(e);
      setLoadError('Не удалось загрузить сотрудников');
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  }, [businessAccountId]);

  useEffect(() => {
    setSelectedEmployeeId(null);
    setSelectedCompanyKey(null);
    setAccessDraft(null);
    setAccessBaseline(null);
    loadEmployees();
  }, [loadEmployees]);

  const selectedEmployee = employees.find((e) => e.id === selectedEmployeeId) || null;

  const myCompanyIds = useMemo(
    () => new Set(accountCompanies.map((c) => String(c.companyId))),
    [accountCompanies]
  );

  const selectedEmployeeCompanies = useMemo(() => {
    if (!selectedEmployee) return [];
    return selectedEmployee.companies.filter((c) => myCompanyIds.has(String(c.companyId)));
  }, [selectedEmployee, myCompanyIds]);

  const availableCompaniesToAdd = accountCompanies.filter((ac) => {
    if (!selectedEmployee) return true;
    return !selectedEmployeeCompanies.some(
      (c) =>
        String(c.companyId) === String(ac.companyId) ||
        (ac.legacyUserId && String(c.legacyUserId) === String(ac.legacyUserId))
    );
  });

  const accessDirty = useMemo(() => {
    if (!accessDraft || !accessBaseline) return false;
    return JSON.stringify(accessDraft) !== JSON.stringify(accessBaseline);
  }, [accessDraft, accessBaseline]);

  const closeCompanyAccess = () => {
    if (accessDirty && !window.confirm('Есть несохранённые изменения. Закрыть без сохранения?')) {
      return;
    }
    setSelectedCompanyKey(null);
    setAccessDraft(null);
    setAccessBaseline(null);
  };

  const accessCompanyName =
    accessDraft?.name ||
    selectedEmployeeCompanies.find(
      (c) => String(c.companyId) === String(selectedCompanyKey)
    )?.name ||
    '';

  const ChevronLeftIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M15 6L9 12L15 18" stroke="#3262FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const openCompanyAccess = async (company) => {
    if (!selectedEmployee || !company) return;
    setAccessLoading(true);
    setSelectedCompanyKey(String(company.companyId));
    try {
      const result = await getEmployeeCompanyAccess(businessAccountId || userId, selectedEmployee.employeeUserId, {
        companyId: company.companyId ? Number(company.companyId) : undefined,
        legacyUserId: company.legacyUserId ? Number(company.legacyUserId) : undefined,
      });
      if (!result.success) {
        alert(result.error || 'Не удалось загрузить доступы');
        setSelectedCompanyKey(null);
        return;
      }
      const draft = {
        companyId: result.company.company_id,
        name: result.company.name,
        ...mapDocAccessFromApi(result.access),
        tasks: !!result.access.tasks,
        chats: (result.chats || []).map((ch) => ({
          id: ch.id,
          title: ch.title,
          enabled: !!ch.enabled,
        })),
      };
      setAccessDraft(draft);
      setAccessBaseline(JSON.parse(JSON.stringify(draft)));
      setSelectedCompanyKey(String(result.company.company_id));
    } catch (e) {
      console.error(e);
      alert('Ошибка загрузки доступов');
      setSelectedCompanyKey(null);
    } finally {
      setAccessLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!canInviteEmployee) {
      if (invitePhoneDigits.length !== 11) {
        alert('Введите 11 цифр номера телефона');
      } else if (employeeLookup && !employeeLookup.found) {
        alert('Пользователь с таким телефоном не зарегистрирован в системе');
      } else if (employeeLookup?.is_self) {
        alert('Нельзя добавить себя');
      } else if (employeeLookup?.already_added) {
        alert('Сотрудник уже добавлен');
      } else {
        alert('Сначала дождитесь проверки номера');
      }
      return;
    }

    setIsLoading(true);
    try {
      const result = await inviteBusinessEmployee(businessAccountId || userId, {
        phone: invitePhoneDigits,
      });
      if (result.success && result.employee) {
        closeAddEmployeeModal();
        await loadEmployees();
      } else {
        const msg = result.error || 'Не удалось добавить';
        const friendly =
          msg === 'employee not found'
            ? 'Пользователь с таким телефоном не зарегистрирован в системе'
            : msg === 'employee already added'
              ? 'Сотрудник уже добавлен'
              : msg === 'cannot add yourself'
                ? 'Нельзя добавить себя'
                : msg;
        alert(friendly);
      }
    } catch (e) {
      console.error(e);
      alert('Ошибка добавления сотрудника');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCompanyToEmployee = async (company) => {
    if (!selectedEmployee || !company) return;
    setIsLoading(true);
    try {
      const result = await assignEmployeeCompany(businessAccountId || userId, selectedEmployee.employeeUserId, {
        companyId: company.companyId ? Number(company.companyId) : undefined,
        legacyUserId: company.legacyUserId ? Number(company.legacyUserId) : undefined,
      });
      if (!result.success) {
        alert(result.error || 'Не удалось назначить компанию');
        return;
      }
      setShowAddCompanyModal(false);
      await loadEmployees();
    } catch (e) {
      console.error(e);
      alert('Ошибка назначения компании');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelAccess = () => {
    if (accessBaseline) {
      setAccessDraft(JSON.parse(JSON.stringify(accessBaseline)));
    }
  };

  const handleSaveAccess = async () => {
    if (!selectedEmployee || !accessDraft) return;
    setAccessSaving(true);
    try {
      const result = await saveEmployeeCompanyAccess({
        business_user_id: businessAccountId || userId,
        employee_user_id: selectedEmployee.employeeUserId,
        company_id: accessDraft.companyId,
        invoices: !!accessDraft.invoices,
        bank: !!accessDraft.bank,
        tax: !!accessDraft.tax,
        salary: !!accessDraft.salary,
        buh_bill: !!accessDraft.buh_bill,
        access_tasks: accessDraft.tasks,
        chat_ids: accessDraft.chats.filter((c) => c.enabled).map((c) => c.id),
      });
      if (!result.success) {
        alert(result.error || 'Не удалось сохранить');
        return;
      }
      const nextBaseline = JSON.parse(JSON.stringify(accessDraft));
      setAccessBaseline(nextBaseline);
      await loadEmployees();
    } catch (e) {
      console.error(e);
      alert('Ошибка сохранения доступов');
    } finally {
      setAccessSaving(false);
    }
  };

  const handleRemoveEmployee = async () => {
    if (!selectedEmployee) return;
    const name = selectedEmployee.displayName || 'пользователя';
    const confirmed = window.confirm(
      `Удалить пользователя ${name} из своего аккаунта?`
    );
    if (!confirmed) return;
    setIsLoading(true);
    try {
      const result = await removeBusinessEmployee(
        businessAccountId || userId,
        selectedEmployee.employeeUserId
      );
      if (!result.success) {
        alert(result.error || 'Не удалось удалить пользователя');
        return;
      }
      setEmployeeMenuOpen(false);
      setSelectedEmployeeId(null);
      setAccessDraft(null);
      setAccessBaseline(null);
      setSelectedCompanyKey(null);
      await loadEmployees();
    } catch (e) {
      console.error(e);
      alert('Ошибка удаления пользователя');
    } finally {
      setIsLoading(false);
    }
  };

  const headerBack = () => {
    if (selectedCompanyKey || accessDraft || accessLoading) {
      if (accessDirty && !window.confirm('Есть несохранённые изменения. Закрыть без сохранения?')) {
        return;
      }
      setSelectedCompanyKey(null);
      setAccessDraft(null);
      setAccessBaseline(null);
      setSelectedEmployeeId(null);
      return;
    }
    if (selectedEmployeeId) {
      setSelectedEmployeeId(null);
      return;
    }
    setHubSection(null);
  };

  const openEditProfile = () => {
    const split = splitPersonName(displayName);
    setFirstName(split.firstName);
    setLastName(split.lastName);
    setEditOpen(true);
    setHubSection(null);
    setSelectedEmployeeId(null);
    setAccessDraft(null);
    setAccessBaseline(null);
    setSelectedCompanyKey(null);
  };

  const closeEditProfile = () => {
    setEditOpen(false);
    setMenuOpen(false);
  };

  const handleExitProfile = () => {
    const accountName = displayName || users?.[0]?.company_name || currentUser?.name || 'аккаунта';
    const confirmed = window.confirm(`Точно выйти из аккаунта ${accountName}?`);
    if (!confirmed) return;

    localStorage.removeItem('userList');
    localStorage.removeItem('user_id');
    dispatch(setUsers([]));
    dispatch(setIsLogOut(true));
    setEditOpen(false);
    setMenuOpen(false);
    navigate('/');
  };

  const applyLocalUserPatch = (patch) => {
    if (currentUser && !currentUser.is_company_context) {
      dispatch(setCurrentUser({ ...currentUser, ...patch }));
    }
    if (Array.isArray(users) && businessAccountId) {
      const nextUsers = users.map((u) =>
        Number(u.user_id) === Number(businessAccountId)
          ? {
              ...u,
              company_name: patch.name != null ? patch.name : u.company_name,
              photo_path: patch.photo_path != null ? patch.photo_path : u.photo_path,
            }
          : u
      );
      dispatch(setUsers(nextUsers));
      localStorage.setItem('userList', JSON.stringify(nextUsers));
    }
    if (patch.name) dispatch(setName(patch.name));
  };

  const handleSaveProfile = async () => {
    const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
    if (!fullName) {
      alert('Укажите имя');
      return;
    }
    if (!businessAccountId) return;
    setProfileSaving(true);
    try {
      const result = await updateUserProfile(businessAccountId, fullName);
      if (!result.success) {
        alert(result.error || 'Не удалось сохранить');
        return;
      }
      const nextName = result.user?.name || fullName;
      const nextPhoto = result.user?.photo_path || photoPath;
      setDisplayName(nextName);
      setPhotoPath(nextPhoto || null);
      applyLocalUserPatch({
        name: nextName,
        photo_path: nextPhoto || null,
      });
      setEditOpen(false);
    } catch (e) {
      console.error(e);
      alert('Ошибка сохранения профиля');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePickPhoto = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !businessAccountId) return;
    setPhotoUploading(true);
    try {
      const result = await uploadUserPhoto(businessAccountId, file);
      if (!result.success) {
        alert(result.error || 'Не удалось загрузить фото');
        return;
      }
      setPhotoPath(result.photo_path);
      applyLocalUserPatch({ photo_path: result.photo_path });
    } catch (err) {
      console.error(err);
      alert('Ошибка загрузки фото');
    } finally {
      setPhotoUploading(false);
    }
  };

  const deleteAccount = async () => {
    const accountId = businessAccountId || userId;
    try {
      if (accountId) {
        const result = await archiveAccountCompanies(accountId);
        if (!result.success) {
          alert(result.error || 'Не удалось архивировать компании аккаунта');
          return;
        }
      }
      createTask(accountId, 'Удалить аккаунт', 'Удалить аккаунт', {
        timeout: new Date(),
        urgency: 1,
      }, () => {});
      setDeleteOpen(false);
      setMenuOpen(false);
    } catch (err) {
      console.error(err);
      alert('Ошибка удаления профиля');
    }
  };

  const headerTitle = selectedEmployee
    ? selectedEmployee.displayName
    : hubSection === 'employees'
      ? 'Пользователи'
      : 'Профиль';

  const showHub = !editOpen && !hubSection;
  const showAvatarHeader = !editOpen && showHub;
  const showEmployeesListHeader =
    !editOpen && hubSection === 'employees' && !selectedEmployee;
  const showEmployeeDetailHeader =
    !editOpen && hubSection === 'employees' && !!selectedEmployee;

  if (hubSection === 'companies') {
    return (
      <div className="main_cntr_chat section-page profile-page profile-page--with-heading">
        <ProfileCompanies onBack={() => setHubSection(null)} />
      </div>
    );
  }

  return (
    <div
      className={`main_cntr_chat section-page profile-page${
        showAvatarHeader ? ' profile-page--hub' : ' profile-page--with-heading'
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handlePhotoSelected}
      />

      {!showAvatarHeader ? (
        <div className="section-page-heading manrope-bold color_white">Профиль</div>
      ) : null}

      {editOpen ? (
        <div className="profile-edit-header">
          <button
            type="button"
            className="page-header-nav-back"
            onClick={closeEditProfile}
            aria-label="Назад"
          >
            {arrowLIcon}
          </button>
          <div className="manrope-bold color_white section-company-title">
            Редактирование профиля
          </div>
          <button
            type="button"
            className="client_title-menu-btn"
            aria-label="Меню"
            onClick={() => setMenuOpen(true)}
          >
            {dots3VerticalIcon}
          </button>
        </div>
      ) : showAvatarHeader ? (
        <div className="profile-hero">
          <div className="profile-hero-main">
            <div className="profile-hero-avatar-wrap">
              <ProfileAvatar name={displayName} photoPath={photoPath} size={80} />
            </div>
            <div className="profile-hero-meta manrope-bold">
              <div className="profile-hero-name">{displayName || '—'}</div>
              <div className="profile-hero-phone">{displayPhone || '—'}</div>
            </div>
            <button
              type="button"
              className="profile-pencil-btn"
              aria-label="Редактировать профиль"
              onClick={openEditProfile}
            >
              {pencilEditIcon}
            </button>
          </div>
        </div>
      ) : showEmployeeDetailHeader ? (
        <div className="chat_page_header section-page_title_cntr profile-employee-header">
          <button
            type="button"
            className="page-header-nav-back"
            onClick={headerBack}
            aria-label="Назад"
          >
            {arrowLIcon}
          </button>
          <div className="profile-employee-header-main">
            <div className="manrope-bold color_white section-company-title">
              {selectedEmployee.displayName}
            </div>
            {selectedEmployee.phone ? (
              <div className="profile-employee-header-phone manrope-bold">
                {selectedEmployee.phone}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="client_title-menu-btn"
            aria-label="Меню"
            onClick={() => setEmployeeMenuOpen(true)}
          >
            {dots3VerticalIcon}
          </button>
        </div>
      ) : showEmployeesListHeader ? (
        <div className="chat_page_header section-page_title_cntr">
          <button
            type="button"
            className="page-header-nav-back"
            onClick={headerBack}
            aria-label="Назад"
          >
            {arrowLIcon}
          </button>
          <div className="manrope-bold color_white section-company-title">{headerTitle}</div>
          <button
            type="button"
            className="client_title-menu-btn"
            aria-label="Меню"
            onClick={() => setEmployeesListMenuOpen(true)}
          >
            {dots3VerticalIcon}
          </button>
        </div>
      ) : (
        <div className="chat_page_header section-page_title_cntr">
          <button
            type="button"
            className="page-header-nav-back"
            onClick={headerBack}
            aria-label="Назад"
          >
            {arrowLIcon}
          </button>
          <div className="manrope-bold color_white section-company-title">{headerTitle}</div>
        </div>
      )}

      <div className="tasks_subcntr section-page-picker profile-body">
        {editOpen ? (
          <div className="profile-edit-form">
            <div className="profile-edit-avatar-block">
              <ProfileAvatar
                name={`${firstName} ${lastName}`.trim() || displayName}
                photoPath={photoPath}
                size={80}
                interactive
                emptyGreen
                onClick={handlePickPhoto}
              />
              {photoUploading ? (
                <div className="profile-avatar-hint profile-avatar-hint--inline manrope-bold">
                  Загрузка…
                </div>
              ) : (
                <div className="profile-avatar-hint profile-avatar-hint--inline manrope-bold">
                  Нажмите, чтобы сменить фото
                </div>
              )}
            </div>
            <label className="profile-field manrope-bold">
              <span>Имя</span>
              <input
                className="input_task manrope-bold"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Имя"
                disabled={profileSaving}
              />
            </label>
            <label className="profile-field manrope-bold">
              <span>Фамилия</span>
              <input
                className="input_task manrope-bold"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Фамилия"
                disabled={profileSaving}
              />
            </label>
            <div className="profile-edit-actions">
              <button
                type="button"
                className="profile-save-btn manrope-bold"
                onClick={handleSaveProfile}
                disabled={profileSaving}
              >
                {profileSaving ? 'Сохранение…' : 'Сохранить'}
              </button>
              <button
                type="button"
                className="profile-exit-btn manrope-bold"
                onClick={handleExitProfile}
                disabled={profileSaving}
              >
                Выйти из профиля
              </button>
            </div>
          </div>
        ) : showHub ? (
          <div className="company-picker-list company-picker-panel profile-hub-list">
            <div className="company-picker-item">
              <button
                type="button"
                className="company-picker-main-btn manrope-bold"
                onClick={() => setHubSection('companies')}
              >
                <div className="company-picker-main-top">
                  <span className="profile-hub-item-label">
                    <span className="profile-hub-item-icon">{addAccountIcon}</span>
                    <span className="company-picker-name">Все компании</span>
                  </span>
                  <span className="company-picker-trailing">{arrowRIcon}</span>
                </div>
              </button>
            </div>
            <div className="company-picker-item">
              <button
                type="button"
                className="company-picker-main-btn manrope-bold"
                onClick={() => setHubSection('employees')}
              >
                <div className="company-picker-main-top">
                  <span className="profile-hub-item-label">
                    <span className="profile-hub-item-icon profile-hub-item-icon--users">
                      {usersGroupIcon}
                    </span>
                    <span className="company-picker-name">Пользователи</span>
                  </span>
                  <span className="company-picker-trailing">{arrowRIcon}</span>
                </div>
              </button>
            </div>
          </div>
        ) : !selectedEmployee ? (
          <>
            {loadError ? (
              <div className="profile-empty manrope-bold">{loadError}</div>
            ) : isLoading && employees.length === 0 ? (
              <div className="profile-empty manrope-bold">Загрузка…</div>
            ) : employees.length === 0 ? (
              <div className="profile-empty manrope-bold">Пользователи пока не добавлены</div>
            ) : (
              <div className="company-picker-list company-picker-panel">
                {employees.map((employee) => (
                  <div key={employee.id} className="company-picker-item">
                    <button
                      type="button"
                      className="company-picker-main-btn manrope-bold"
                      onClick={() => setSelectedEmployeeId(employee.id)}
                    >
                      <div className="company-picker-main-top">
                        <span className="company-picker-name">{employee.displayName}</span>
                        <span className="company-picker-trailing profile-employee-trailing">
                          {employee.phone ? (
                            <span className="profile-employee-meta profile-employee-meta--inline">
                              Тел: {employee.phone}
                            </span>
                          ) : null}
                          {arrowRIcon}
                        </span>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : accessDraft || accessLoading ? (
          <div className="profile-access-settings">
            <div className="profile-user-access-nav">
              <div className="profile-user-access-nav-row">
                <button
                  type="button"
                  className="profile-user-access-back"
                  onClick={closeCompanyAccess}
                  aria-label="Назад к списку компаний"
                >
                  <ChevronLeftIcon />
                </button>
                <div className="profile-company-access-name manrope-bold profile-company-access-heading">
                  {accessCompanyName || 'Компания'}
                </div>
              </div>
              <div className="profile-settings-title manrope-bold profile-company-access-section-title">
                Доступ к разделам сервиса
              </div>
            </div>
            {accessLoading || !accessDraft ? (
              <div className="profile-empty manrope-bold">Загрузка доступов…</div>
            ) : (
              <>
                <div className="profile-access-block">
                  <div className="profile-access-subtitle manrope-bold">Документы</div>
                  {DOC_ACCESS_SECTIONS.map((section) => (
                    <label key={section.id} className="profile-access-row">
                      <span>{section.title}</span>
                      <input
                        type="checkbox"
                        checked={!!accessDraft[section.id]}
                        onChange={(e) =>
                          setAccessDraft((prev) => ({
                            ...prev,
                            [section.id]: e.target.checked,
                          }))
                        }
                      />
                    </label>
                  ))}
                </div>

                <div className="profile-access-block">
                  <label className="profile-access-heading-row">
                    <span className="profile-access-subtitle manrope-bold">Задачи</span>
                    <input
                      type="checkbox"
                      checked={accessDraft.tasks}
                      onChange={(e) =>
                        setAccessDraft((prev) => ({ ...prev, tasks: e.target.checked }))
                      }
                    />
                  </label>
                </div>

                <div className="profile-access-block">
                  <div className="profile-access-subtitle manrope-bold">Чаты</div>
                  {accessDraft.chats.length === 0 ? (
                    <div className="profile-empty profile-empty--compact manrope-bold">
                      Чаты компании пока не созданы
                    </div>
                  ) : (
                    accessDraft.chats.map((chat) => (
                      <label key={chat.id} className="profile-access-row">
                        <span>{chat.title}</span>
                        <input
                          type="checkbox"
                          checked={chat.enabled}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            setAccessDraft((prev) => ({
                              ...prev,
                              chats: prev.chats.map((c) =>
                                c.id === chat.id ? { ...c, enabled } : c
                              ),
                            }));
                          }}
                        />
                      </label>
                    ))
                  )}
                </div>

                {accessDirty && (
                  <div className="profile-access-actions">
                    <button
                      type="button"
                      className="profile-add-company-btn manrope-bold"
                      style={{ background: '#94A3B8' }}
                      onClick={handleCancelAccess}
                      disabled={accessSaving}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="profile-add-company-btn manrope-bold"
                      onClick={handleSaveAccess}
                      disabled={accessSaving}
                    >
                      {accessSaving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="profile-employee-settings">
            <div className="profile-settings-title profile-settings-title--center manrope-bold">
              Доступные компании
            </div>
            {selectedEmployeeCompanies.length === 0 ? (
              <div className="profile-empty profile-empty--compact manrope-bold">
                Компании не назначены
              </div>
            ) : (
              <div className="company-picker-list company-picker-panel">
                {selectedEmployeeCompanies.map((company) => (
                  <div
                    key={company.companyId}
                    className="company-picker-item company-picker-item--profile-company"
                  >
                    <button
                      type="button"
                      className="company-picker-main-btn company-picker-main-btn--profile-company manrope-bold"
                      onClick={() => openCompanyAccess(company)}
                    >
                      <span className="company-picker-trailing">{arrowRIcon}</span>
                      <span className="company-picker-name">{company.name}</span>
                      {company.inn ? (
                        <div className="profile-employee-meta">ИНН: {company.inn}</div>
                      ) : null}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              className="profile-add-company-btn manrope-bold"
              onClick={() => setShowAddCompanyModal(true)}
            >
              Добавить компанию
            </button>
          </div>
        )}
      </div>

      {employeesListMenuOpen && (
        <ClientMenuModal
          onClose={() => setEmployeesListMenuOpen(false)}
          items={[
            {
              label: 'Добавить пользователя',
              icon: (
                <span className="profile-menu-users-icon">
                  {iconWithPlus(usersGroupIcon)}
                </span>
              ),
              onClick: () => setShowAddEmployeeModal(true),
            },
          ]}
        />
      )}

      {menuOpen && (
        <ClientMenuModal
          onClose={() => setMenuOpen(false)}
          items={[
            {
              label: 'Удалить профиль',
              icon: <span className="profile-menu-delete-icon">{deleteIcon}</span>,
              danger: true,
              onClick: () => setDeleteOpen(true),
            },
          ]}
        />
      )}

      {employeeMenuOpen && selectedEmployee && (
        <ClientMenuModal
          onClose={() => setEmployeeMenuOpen(false)}
          items={[
            {
              label: 'Удалить пользователя из своего аккаунта',
              icon: <span className="profile-menu-delete-icon">{deleteIcon}</span>,
              danger: true,
              onClick: handleRemoveEmployee,
            },
          ]}
        />
      )}

      {deleteOpen && (
        <DeleteModal onClose={() => setDeleteOpen(false)} deleteCurrentUser={deleteAccount} />
      )}

      {showAddEmployeeModal && (
        <div className="modal-container profile-add-employee-modal profile-narrow-modal">
          <div className="modal-content">
            <div className="close-button" onClick={closeAddEmployeeModal}>
              {closeIconTask}
            </div>
            <div style={{ textAlign: 'center' }} className="manrope-bold color_blue_1 fs-20">
              Добавить пользователя
            </div>
            <div style={{ width: '100%', marginTop: '25px' }}>
              <div className="manrope-bold" style={{ fontSize: '16px', textAlign: 'left' }}>
                Номер телефона
              </div>
            </div>
            <input
              className="input_task manrope-bold"
              placeholder="Телефон (логин)"
              value={inviteValue}
              onChange={(e) => setInviteValue(e.target.value)}
              disabled={isLoading}
              inputMode="tel"
              autoComplete="tel"
            />
            <div className="profile-lookup manrope-bold">
              {invitePhoneDigits.length < 11 && (
                <div className="profile-lookup-hint">Введите 11 цифр номера</div>
              )}
              {invitePhoneDigits.length === 11 && lookupLoading && (
                <div className="profile-lookup-hint">Поиск…</div>
              )}
              {invitePhoneDigits.length === 11 &&
                !lookupLoading &&
                employeeLookup?.success !== false &&
                employeeLookup &&
                !employeeLookup.found && (
                <div className="profile-lookup-status profile-lookup-status--warn">
                  Пользователь с таким телефоном не зарегистрирован в системе
                </div>
              )}
              {invitePhoneDigits.length === 11 && !lookupLoading && employeeLookup?.success === false && (
                <div className="profile-lookup-status profile-lookup-status--warn">
                  {employeeLookup.error || 'Не удалось проверить номер'}
                </div>
              )}
              {invitePhoneDigits.length === 11 && !lookupLoading && employeeLookup?.found && (
                <div className="profile-lookup-card">
                  <ProfileAvatar
                    name={employeeLookup.user?.name || employeeLookup.user?.phone}
                    photoPath={employeeLookup.user?.photo_path}
                    size={56}
                  />
                  <div className="profile-lookup-meta">
                    <div className="profile-lookup-name">
                      {employeeLookup.user?.name || 'Без имени'}
                    </div>
                    <div className="profile-lookup-phone">
                      {employeeLookup.user?.phone || invitePhoneDigits}
                    </div>
                    {employeeLookup.is_self && (
                      <div className="profile-lookup-status profile-lookup-status--warn">
                        Это ваш номер — себя добавить нельзя
                      </div>
                    )}
                    {!employeeLookup.is_self && employeeLookup.already_added && (
                      <div className="profile-lookup-status profile-lookup-status--warn">
                        Уже в списке сотрудников
                      </div>
                    )}
                    {!employeeLookup.is_self && !employeeLookup.already_added && (
                      <div className="profile-lookup-status profile-lookup-status--ok">
                        Можно добавить
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <button
                type="button"
                className="create-button manrope-bold"
                onClick={handleAddEmployee}
                disabled={isLoading || !canInviteEmployee}
              >
                {isLoading ? 'ДОБАВЛЕНИЕ…' : 'ДОБАВИТЬ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddCompanyModal && (
        <div className="modal-container profile-narrow-modal">
          <div className="modal-content">
            <div className="close-button" onClick={() => setShowAddCompanyModal(false)}>
              {closeIconTask}
            </div>
            <div style={{ textAlign: 'center' }} className="manrope-bold color_blue_1 fs-20">
              Выберите компанию
            </div>
            <div style={{ width: '100%', marginTop: '20px' }}>
              {availableCompaniesToAdd.length === 0 ? (
                <div className="profile-empty profile-empty--compact manrope-bold">
                  Нет доступных компаний. Добавьте компанию по ИНН в разделе Компании.
                </div>
              ) : (
                <div className="company-picker-list company-picker-panel">
                  {availableCompaniesToAdd.map((company) => (
                    <div
                      key={company.companyId || company.legacyUserId}
                      className="company-picker-item company-picker-item--profile-company"
                    >
                      <button
                        type="button"
                        className="company-picker-main-btn company-picker-main-btn--profile-company manrope-bold"
                        onClick={() => handleAddCompanyToEmployee(company)}
                        disabled={isLoading}
                      >
                        <span className="company-picker-trailing">{arrowRIcon}</span>
                        <span className="company-picker-name">{company.name}</span>
                        {company.inn ? (
                          <div className="profile-employee-meta">ИНН: {company.inn}</div>
                        ) : null}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
